import { type ReactElement, type ReactNode, useState } from 'react';
import cx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, DropZone, ExternalLink, type Item, List } from '@repo/design-system-kit';
import { Accordion } from '../../accordion';
import styles from './release-documents.module.css';

function renderLink(chunks: ReactNode, url: string): ReactElement {
  return (
    <ExternalLink
      className={styles.certificateLink}
      href={url}
      rel="noopener noreferrer"
      target="_blank"
    >
      {chunks}
    </ExternalLink>
  );
}
const renderItalics = (chunks: ReactNode[]): ReactNode => {
  return <i>{chunks}</i>;
};

const fixedCapitalListItems: Item[] = [
  {
    icon: 'bullet',
    key: 'fixed-item-1',
    text: (
      <span className="body-standard">
        <FormattedMessage
          id="company-creation-kit.capital-increase.deposit-certificate.document-type.fixed-capital.description"
          values={{ i: renderItalics }}
        />
      </span>
    ),
  },
];

const variableCapitalListItems: Item[] = [
  {
    icon: 'bullet',
    key: 'variable-item-1',
    text: (
      <span className="body-standard">
        <FormattedMessage
          id="company-creation-kit.capital-increase.deposit-certificate.document-type.variable-capital.description.item1"
          values={{ i: renderItalics }}
        />
      </span>
    ),
  },
  {
    icon: 'bullet',
    key: 'variable-item-2',
    text: (
      <span className="body-standard">
        <FormattedMessage
          id="company-creation-kit.capital-increase.deposit-certificate.document-type.variable-capital.description.item2"
          values={{ i: renderItalics }}
        />
      </span>
    ),
  },
];

export interface ReleaseDocumentsProps {
  certificateDownloadUrl: string;
  onFileUpload: (files: File[]) => void;
  onSubmit: () => void;
  submitIsLoading: boolean;
}

export function ReleaseDocuments({
  certificateDownloadUrl,
  onFileUpload,
  onSubmit,
  submitIsLoading,
}: ReleaseDocumentsProps): ReactElement {
  const { formatMessage } = useIntl();
  const [uploadedFile, setUploadedFile] = useState<File[]>();
  const [customError, setCustomError] = useState<string>();

  function uploadFile(files: File[]): void {
    setUploadedFile(files);
    setCustomError(undefined);
    onFileUpload(files);
  }

  function createDocument(): void {
    if (!uploadedFile) {
      setCustomError(
        formatMessage({
          id: 'company-creation-kit.capital-increase.deposit-certificate.completion-document-upload.missing',
        })
      );
      return;
    }

    onSubmit();
  }

  return (
    <div className={styles.pageContainer}>
      <h1 className="heading-section mb-16">
        <FormattedMessage id="company-creation-kit.capital-increase.deposit-certificate.title" />
      </h1>

      <p className="body-standard mb-32">
        <FormattedMessage
          id="company-creation-kit.capital-increase.deposit-certificate.subtitle"
          values={{
            link: chunks => renderLink(chunks, certificateDownloadUrl),
          }}
        />
      </p>

      <Accordion
        className={cx(styles.accordion, 'mb-32')}
        title={formatMessage({
          id: 'company-creation-kit.capital-increase.deposit-certificate.document-type.title',
        })}
      >
        <p className="body-standard mb-8">
          <FormattedMessage id="company-creation-kit.capital-increase.deposit-certificate.document-type.fixed-capital.title" />
        </p>

        <List className="mb-16" items={fixedCapitalListItems} />

        <p className="body-standard mb-8">
          <FormattedMessage id="company-creation-kit.capital-increase.deposit-certificate.document-type.variable-capital.title" />
        </p>

        <List items={variableCapitalListItems} />
      </Accordion>

      <div className="mb-32">
        <p className="body-standard">
          <FormattedMessage id="company-creation-kit.capital-increase.deposit-certificate.completion-document-upload.label" />
        </p>

        <DropZone
          acceptedFileTypes={['application/pdf']}
          customErrorMessage={customError}
          onFileRemove={() => {
            setUploadedFile(undefined);
          }}
          onFilesSelect={uploadFile}
          placeholder={formatMessage({
            id: 'company-creation-kit.capital-increase.deposit-certificate.completion-document-upload.dropzone',
          })}
        />
      </div>

      <Button
        data-testid="release-documents-submit-button"
        isLoading={submitIsLoading}
        onPress={createDocument}
      >
        <FormattedMessage id="company-creation-kit.capital-increase.deposit-certificate.cta" />
      </Button>
    </div>
  );
}
